import styled from "@emotion/styled"
import { Typography } from "@mui/material"
import * as api from "api"
import RatioElement from "common/ElementRatio"
import { useIsDesktop } from "common/MediaQueries"
import NextLink from "common/NextLink"
import ResponsiveModelImage from "common/ResponsiveModelImage"
import { formatCurrency } from "common/utils/formatters"
import slugify from "common/utils/slugify"
import { memo } from "react"
import Shared from "theme/shared"

const Styled = {
  Vehicle: styled.div`
    margin: 0 auto;
    width: 100%;
    max-width: 1140px;
    box-shadow: 0 10px 20px 0 ${(p) => p.theme.colors.darkIndigo};
    border: solid 1px ${(p) => p.theme.colors.darkIndigo};
    background-color: ${(p) => p.theme.colors.white};

    display: flex;
    flex-direction: column;

    &:not(:last-of-type) {
      margin-bottom: 90px;
    }

    padding: 0 15px 26px;

    ${(p) => p.theme.breakpoints.up("md")} {
      padding: 34px 23px;
      flex-direction: row;
      flex-wrap: wrap;
      &:not(:last-of-type) {
        margin-bottom: 33px;
      }
    }
  `,
  Left: styled.div`
    ${(p) => p.theme.breakpoints.down("sm")} {
      order: 3;
    }
    ${(p) => p.theme.breakpoints.up("md")} {
      padding-top: 18px;
      flex-basis: 75%;
    }
  `,
  Right: styled.div`
    text-align: center;
    ${(p) => p.theme.breakpoints.down("sm")} {
      order: 1;
      margin-top: -30px;
    }
    ${(p) => p.theme.breakpoints.up("md")} {
      flex-basis: 25%;
    }
  `,
  Row: styled.div`
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    justify-items: center;
    padding-inline-start: 20px;
    gap: 8px;
    border-top: 1px solid rgba(135, 148, 161, 0.1);
    padding: 12px 0 9px;
    flex-wrap: wrap;
    &:last-of-type {
      border-bottom: 1px solid rgba(135, 148, 161, 0.1);
    }

    ${(p) => p.theme.breakpoints.up("md")} {
      padding: 8px 0;
      flex-wrap: nowrap;
      margin-inline-start: 15px;
    }
  `,
  Column: styled.div`
    flex: 1;
    text-align: center;
    &.name {
      flex: 1.3;
    }
    ${(p) => p.theme.breakpoints.down("sm")} {
      &.price {
        margin-top: 2px;
        text-align: left; /* flipped by mui */
        flex-basis: 100%;
      }
    }
  `,

  Title: styled(Typography)`
    font-size: 30px;
    font-weight: bold;
    text-align: left; /* flipped by mui */
    line-height: 1.6;
    flex-basis: 100%;
    text-transform: uppercase;
    color: ${(p) => p.theme.colors.marine};
    ${(p) => p.theme.breakpoints.down("sm")} {
      order: 2;
    }
    ${(p) => p.theme.breakpoints.up("md")} {
      color: ${(p) => p.theme.colors.oceanBlue};
      line-height: 1;
      font-size: 30px;
      margin-inline-start: calc(25% + 15px);
    }
  `,
  Label: styled(Typography)`
    white-space: nowrap;
    font-size: 12px;
    color: rgba(1, 21, 46, 0.8);
    color: ${(p) => p.theme.colors.marine};
    margin-bottom: 6px;
    ${(p) => p.theme.breakpoints.down("sm")} {
      .price & {
        font-size: 14px;
        margin-inline-end: 4px;
        display: inline-block;
      }
    }
    ${(p) => p.theme.breakpoints.up("md")} {
      font-size: 18px;
    }
  `,
  Value: styled(Typography)`
    font-size: 12px;
    color: ${(p) => p.theme.colors.bluishGrey};

    b {
      color: ${(p) => p.theme.colors.marine};
    }

    ${(p) => p.theme.breakpoints.down("sm")} {
      .price & {
        font-size: 14px;
        display: inline-block;
        color: ${(p) => p.theme.colors.marine};
      }
    }
    ${(p) => p.theme.breakpoints.up("md")} {
      font-size: 16px;
    }
  `,
  Button: styled(Shared.Button)`
    ${(p) => p.theme.breakpoints.down("sm")} {
      padding: 0;
    }
    ${(p) => p.theme.breakpoints.up("md")} {
      padding: 8px 60px;
    }
  `,
  Buttons: styled.div`
    order: 4;
  `,
}

type children = (api.VehicleChildren | api.VehicleFinishingLevel)[]

type VehicleModelExtendedProps = {
  model: api.Vehicle | api.CustomVehicle | api.FVehicle | api.TransitVehicle
}

const VehicleModelExtended = ({ model }: VehicleModelExtendedProps) => {
  const childModels = (model as api.CustomVehicle).vehicleChildModels
  model = model as api.Vehicle
  const link = `/${model.id}/${slugify(model.name)}`
  const isDesktop = useIsDesktop()

  return (
    <Styled.Vehicle>
      <Styled.Title>
        <NextLink href={link}>{model.name}</NextLink>
      </Styled.Title>
      <Styled.Right>
        <RatioElement ratio={290 / 186}>
          <NextLink href={link}>
            <ResponsiveModelImage
              image={model.image}
              imageSizes={["100vw", "273px"]}
            />
          </NextLink>
        </RatioElement>
        <Shared.Desktop>
          <NextLink href={link} passHref>
            <Styled.Button variant="outlined">לפרטים נוספים</Styled.Button>
          </NextLink>
        </Shared.Desktop>
      </Styled.Right>
      <Styled.Left>
        {/* headlines */}
        <Shared.Desktop>
          <Styled.Row>
            <Styled.Column className="name">
              <Styled.Label>שם דגם</Styled.Label>
            </Styled.Column>
            <Styled.Column>
              <Styled.Label>מחיר לא כולל מע״מ</Styled.Label>
            </Styled.Column>
            <Styled.Column>
              <Styled.Label>מחיר כולל מע״מ</Styled.Label>
            </Styled.Column>
            <Styled.Column>
              <Styled.Label>אגרת רישוי</Styled.Label>
            </Styled.Column>
            <Styled.Column className="price">
              <Styled.Label>
                <b>סה״כ מחיר</b>
              </Styled.Label>
            </Styled.Column>
          </Styled.Row>
        </Shared.Desktop>
        {(childModels as children).map((child, i) => (
          <Styled.Row key={i}>
            <Styled.Column className="name">
              <Shared.Mobile>
                <Styled.Label>שם דגם</Styled.Label>
              </Shared.Mobile>
              <Styled.Value>{child.name}</Styled.Value>
            </Styled.Column>
            <Styled.Column>
              <Shared.Mobile>
                <Styled.Label>מחיר לא כולל מע״מ</Styled.Label>
              </Shared.Mobile>
              <Styled.Value>
                ₪{formatCurrency(child.priceWithoutVAT)}
              </Styled.Value>
            </Styled.Column>
            <Styled.Column>
              <Shared.Mobile>
                <Styled.Label>מחיר כולל מע״מ</Styled.Label>
              </Shared.Mobile>
              <Styled.Value>₪{formatCurrency(child.priceWithVAT)}</Styled.Value>
            </Styled.Column>
            <Styled.Column>
              <Shared.Mobile>
                <Styled.Label>אגרת רישוי</Styled.Label>
              </Shared.Mobile>
              <Styled.Value>₪{formatCurrency(child.licensingFee)}</Styled.Value>
            </Styled.Column>
            <Styled.Column className="price">
              {!isDesktop && (
                <Styled.Label>
                  <b>סה״כ מחיר</b>
                </Styled.Label>
              )}
              <Styled.Value>
                <b>
                  ₪{formatCurrency(child.priceWithVAT + child.licensingFee)}
                </b>
              </Styled.Value>
            </Styled.Column>
          </Styled.Row>
        ))}
      </Styled.Left>
      {!isDesktop && (
        <Styled.Buttons>
          <NextLink href={link} passHref>
            <Styled.Button variant="outlined">לפרטים נוספים</Styled.Button>
          </NextLink>
        </Styled.Buttons>
      )}
    </Styled.Vehicle>
  )
}

export default memo(VehicleModelExtended)
